import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle, Sparkles, User, Trees, Flower2, Activity } from 'lucide-react';
import api from '../api';
import SEO from './SEO';
import { Link } from 'react-router-dom';
import contactBg from '../assets/tropical_flowers_bg.webp';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [status, setStatus] = useState({ loading: false, success: false, error: null });


    useEffect(() => {
        window.scrollTo(0, 0);
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            const user = JSON.parse(savedUser);
            setIsLoggedIn(true);
            setFormData(prev => ({
                ...prev,
                name: user.full_name || user.name || '',
                email: user.email || ''
            }));
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone') {
            const cleanValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: cleanValue }));
        } else if (name === 'name') {
            if (value === '' || /^[a-zA-Z\s]*$/.test(value)) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'message') {
            const words = value.trim().split(/\s+/);
            if (words.length <= 200 || value.length < formData.message.length) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const wordCount = formData.message.trim() ? formData.message.trim().split(/\s+/).length : 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, success: false, error: null });
        const submissionData = { ...formData };

        try {
            const response = await api.post('/contact', submissionData);
            if (response.data.status === 'success') {
                setStatus({ loading: false, success: true, error: null });
                setFormData({ name: '', email: '', phone: '', message: '' });
            } else {
                setStatus({ loading: false, success: false, error: response.data.message || 'Submission failed' });
            }
        } catch (err) {
            console.error('Contact submission error:', err);
            const errorMsg = err.response?.data?.detail || err.message || 'Transmission failed. Please retry.';
            setStatus({ loading: false, success: false, error: errorMsg });
        }
    };

    return (
        <section className="relative min-h-screen pt-32 pb-20 flex items-center justify-center overflow-hidden">
            <SEO 
                title="Contact Us" 
                description="Get in touch with our travel designers at Sunshine Travels. Request a custom quote, inquire about tour packages, or visit our office. We are here to bring your dream trip to life." 
                canonicalPath="/contact" 
            />
            {/* Beautiful Tropical Background */}
            <div className="absolute inset-0 z-0">
                <img
                    src={contactBg}
                    alt="Serene Mountain Valley"
                    className="w-full h-full object-cover hd-image"
                />
                <div className="absolute inset-0 bg-slate-900/10 backdrop-blur-md"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-transparent to-slate-900/50"></div>
            </div>

            {/* Futuristic Floating Service Quick Access */}
            <div className="fixed top-28 right-4 md:top-32 md:right-8 z-50 w-24 h-36 md:w-32 md:h-44 hidden md:block">
                
                {/* Wildlife: Top Right */}
                <div onClick={() => window.open('https://adventure.sunshinemanipal.com', '_blank')} className="absolute top-0 right-0 group flex items-center justify-end cursor-pointer">
                <span className="absolute right-[120%] opacity-0 group-hover:opacity-100 group-hover:-translate-x-1 transition-all duration-300 bg-white text-orange-600 text-[10px] md:text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-2xl shadow-2xl whitespace-nowrap border border-orange-100 pointer-events-none">
                    Wildlife Safaris
                </span>
                <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white shadow-[0_10px_30px_rgba(249,115,22,0.4)] border-4 border-white/20 hover:border-white transition-all overflow-hidden relative">
                    <Trees size={24} className="relative z-10 group-hover:scale-110 transition-transform" />
                    <div className="absolute inset-0 bg-white/20 -translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-full"></div>
                </div>
                </div>

                {/* Yoga: Center Left */}
                <div onClick={() => window.open('https://mysticroutes.sunshinemanipal.com/', '_blank')} className="absolute top-1/2 -translate-y-1/2 left-0 group flex items-center justify-end cursor-pointer">
                <span className="absolute right-[120%] opacity-0 group-hover:opacity-100 group-hover:-translate-x-1 transition-all duration-300 bg-white text-orange-600 text-[10px] md:text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-2xl shadow-2xl whitespace-nowrap border border-orange-100 pointer-events-none">
                    Yoga & Wellness
                </span>
                <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white shadow-[0_10px_30px_rgba(249,115,22,0.4)] border-4 border-white/20 hover:border-white transition-all overflow-hidden relative">
                    <Flower2 size={24} className="relative z-10 group-hover:scale-110 transition-transform" />
                    <div className="absolute inset-0 bg-white/20 -translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-full"></div>
                </div>
                </div>

                {/* Medical: Bottom Right */}
                <div onClick={() => window.open('https://cureconnect.sunshinemanipal.com', '_blank')} className="absolute bottom-0 right-0 group flex items-center justify-end cursor-pointer">
                <span className="absolute right-[120%] opacity-0 group-hover:opacity-100 group-hover:-translate-x-1 transition-all duration-300 bg-white text-orange-600 text-[10px] md:text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-2xl shadow-2xl whitespace-nowrap border border-orange-100 pointer-events-none">
                    Medical Tourism
                </span>
                <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-600 to-orange-700 rounded-full flex items-center justify-center text-white shadow-[0_10px_30px_rgba(249,115,22,0.4)] border-4 border-white/20 hover:border-white transition-all overflow-hidden relative">
                    <Activity size={24} className="relative z-10 group-hover:scale-110 transition-transform" />
                    <div className="absolute inset-0 bg-white/20 -translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-full"></div>
                </div>
                </div>
            </div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/10 backdrop-blur-3xl rounded-[3rem] border border-white/30 overflow-hidden shadow-2xl flex flex-col md:flex-row"
                    >
                        {/* Left Side: Simple Info */}
                        <div className="md:w-[40%] p-10 space-y-8 text-white relative">
                            <div className="space-y-4">
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500 text-[10px] font-bold uppercase tracking-widest shadow-lg">
                                    <Sparkles size={12} /> Contact Us
                                </span>
                                <h1 className="text-4xl md:text-6xl font-serif font-bold leading-snug tracking-wide">
                                    Plan Your <br /><span className="text-orange-400">Dream</span> <br />Holiday.
                                </h1>
                            </div>

                            <div className="space-y-10 pt-6">
                                <div>
                                    <div className="flex items-center gap-3 text-orange-400 mb-1">
                                        <Phone size={16} />
                                        <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">Phone</span>
                                    </div>
                                    <p className="text-sm font-bold">+91 98802 32901</p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-3 text-orange-400 mb-1">
                                        <Mail size={16} />
                                        <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">Email</span>
                                    </div>
                                    <p className="text-sm font-bold pr-10">sunshinetravelsmanipal@gmail.com</p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-3 text-orange-400 mb-1">
                                        <MapPin size={16} />
                                        <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">Address</span>
                                    </div>
                                    <p className="text-sm font-bold leading-snug">Tiger Circle, Manipal, Karnataka</p>
                                </div>
                            </div>


                        </div>

                        {/* Right Side: Easy Form */}
                        <div className="md:w-[65%] p-10 lg:p-14 bg-white/90 backdrop-blur-xl">
                            <AnimatePresence mode="wait">
                                {status.success ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="h-full flex flex-col items-center justify-center text-center space-y-6"
                                    >
                                        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 shadow-lg">
                                            <CheckCircle size={32} />
                                        </div>
                                        <div className="space-y-2">
                                            <h3 className="text-2xl font-black text-slate-900 uppercase">Message Sent</h3>
                                            <p className="text-slate-500 font-medium">We will contact you very soon.</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-8">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-slate-500">
                                                    <User size={14} />
                                                    <label className="text-[10px] font-bold uppercase tracking-widest">Your Name</label>
                                                </div>
                                                <input
                                                    type="text" name="name" required value={formData.name} onChange={handleChange}
                                                    placeholder="Enter your name"
                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:border-orange-500 focus:bg-white outline-none transition-all font-bold text-slate-900"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-slate-500">
                                                    <Mail size={14} />
                                                    <label className="text-[10px] font-bold uppercase tracking-widest">Email Address</label>
                                                </div>
                                                <input
                                                    type="email" name="email" required
                                                    readOnly={isLoggedIn && formData.email !== ''}
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="Enter your email"
                                                    className={`w-full border border-slate-200 rounded-xl px-4 py-3 outline-none transition-all font-bold text-slate-900 ${isLoggedIn && formData.email !== '' ? 'bg-slate-100 opacity-70 cursor-not-allowed' : 'bg-slate-50 focus:border-orange-500 focus:bg-white'}`}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-slate-500">
                                                <Phone size={14} />
                                                <label className="text-[10px] font-bold uppercase tracking-widest">Phone Number</label>
                                            </div>
                                            <input
                                                type="tel" name="phone" required placeholder="10-digit number" value={formData.phone} onChange={handleChange}
                                                maxLength={10}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:border-orange-500 focus:bg-white outline-none transition-all font-bold text-slate-900"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between text-slate-500">
                                                <div className="flex items-center gap-2">
                                                    <Send size={14} />
                                                    <label className="text-[10px] font-bold uppercase tracking-widest">Your Message</label>
                                                </div>
                                                <span className={`text-[10px] font-bold uppercase tracking-widest ${wordCount >= 200 ? 'text-red-500' : 'text-slate-400'}`}>
                                                    {wordCount}/200 words
                                                </span>
                                            </div>
                                            <textarea
                                                name="message" required rows="3" value={formData.message} onChange={handleChange}
                                                placeholder="Tell us where you want to go..."
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:border-orange-500 focus:bg-white outline-none transition-all font-bold text-slate-900 resize-none"
                                            ></textarea>
                                        </div>

                                        {status.error && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="bg-red-50 text-red-500 p-4 rounded-xl text-xs font-bold text-center"
                                            >
                                                {status.error}
                                            </motion.div>
                                        )}

                                        <div className="flex flex-wrap gap-4">
                                            <button
                                                type="submit" disabled={status.loading}
                                                className="px-10 bg-slate-900 text-white py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-orange-500 transition-all shadow-lg flex items-center justify-center gap-3"
                                            >
                                                {status.loading ? 'Sending...' : <>Send Message <Send size={16} /></>}
                                            </button>

                                            <Link
                                                to="/customised-destination"
                                                className="px-8 border-2 border-slate-900 text-slate-900 py-4 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-900 hover:text-white transition-all flex items-center justify-center gap-2 group shadow-sm"
                                            >
                                                Customize Your Travel <Sparkles size={14} className="text-orange-500 group-hover:text-white transition-colors" />
                                            </Link>
                                        </div>
                                    </form>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
